import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import api from "@/lib/api";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let configPromise = null;
export const getTurnstileConfig = (fresh = false) => {
  if (!configPromise || fresh) configPromise = api.get("/auth/turnstile-config").then((r) => r.data).catch(() => ({ site_key: "", required: false }));
  return configPromise;
};

function loadScript() {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve();
    let s = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = SCRIPT_SRC; s.async = true; s.defer = true;
      document.head.appendChild(s);
    }
    const t = setInterval(() => { if (window.turnstile) { clearInterval(t); resolve(); } }, 50);
  });
}

// Renders an explicit Turnstile widget. Parent gets the single-use token via onToken and must call ref.reset() after each attempt.
const TurnstileWidget = forwardRef(function TurnstileWidget({ action, onToken, onReady }, ref) {
  const box = useRef(null);
  const widgetId = useRef(null);
  const [cfg, setCfg] = useState(null);
  const tokenCb = useRef(onToken);
  tokenCb.current = onToken;
  const readyCb = useRef(onReady);
  readyCb.current = onReady;

  useEffect(() => { getTurnstileConfig(true).then((c) => { setCfg(c); readyCb.current?.(c); }); }, []);

  const reset = useCallback(() => {
    tokenCb.current(null);
    if (widgetId.current != null && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);
  useImperativeHandle(ref, () => ({ reset }), [reset]);

  useEffect(() => {
    if (!cfg?.site_key) return undefined;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !box.current) return;
      widgetId.current = window.turnstile.render(box.current, {
        sitekey: cfg.site_key, action, theme: "light", size: "flexible",
        callback: (t) => tokenCb.current(t),
        "expired-callback": () => tokenCb.current(null),
        "timeout-callback": () => tokenCb.current(null),
        "error-callback": () => tokenCb.current(null),
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current != null && window.turnstile) { window.turnstile.remove(widgetId.current); widgetId.current = null; }
    };
  }, [cfg, action]);

  if (!cfg?.site_key) return null;
  return <div ref={box} data-testid={`turnstile-${action}`} aria-label="Security verification" className="min-h-[65px]" />;
});

export default TurnstileWidget;

// Shared hook: token state + submit gating + reset helper for a form with one widget.
export function useTurnstile() {
  const ref = useRef(null);
  const [token, setToken] = useState(null);
  const [required, setRequired] = useState(false);
  const onReady = useCallback((c) => setRequired(!!c.required), []);
  const blocked = required && !token;
  const reset = () => ref.current?.reset();
  return { ref, token, setToken, onReady, blocked, reset };
}
